'use client';

import { BadgeCheck } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { LandingButton, LandingCard } from '@/components/landing/shared';

export type BillingCycle = 'monthly' | 'annual';

export interface PricingPlan {
  title: string;
  price: string;
  originalPrice?: string;
  period?: string;
  annualTotalText?: string;
  annualSavingsText?: string;
  description: string;
  features: string[];
  highlightedFeatures?: string[];
  bgClass?: string;
  isPopular?: boolean;
  /** Renders the card without a price block (e.g. contact-sales tiers). */
  withoutPrice?: boolean;
  ctaText: string;
  /** Link the CTA opens. Omit when the CTA runs `onAction` instead. */
  href?: string;
  /** Async action the CTA runs instead of navigating (e.g. starting a checkout). */
  onAction?: () => void;
  /** Disables the CTA and shows a spinner while `onAction` is running. */
  isActionLoading?: boolean;
  /** Shown under the CTA when `onAction` failed, so the visitor can retry. */
  actionError?: string;
}

interface PricingCarouselProps {
  plans: PricingPlan[];
  billingCycle: BillingCycle;
  showBillingToggle?: boolean;
  onBillingCycleChange?: (cycle: BillingCycle) => void;
  onCTAClick?: (plan: PricingPlan) => void;
  className?: string;
  title?: string;
  description?: string;
}

const PricingFeature = ({
  text,
  isHighlighted = false,
}: {
  text: string;
  isHighlighted?: boolean;
}) => (
  <li className="flex items-start gap-2 py-1">
    <BadgeCheck
      size={18}
      className={`shrink-0 mt-0.5 ${isHighlighted ? 'text-neutral-900' : 'text-neutral-400'}`}
    />
    <span
      className={`text-sm leading-snug ${isHighlighted ? 'font-semibold text-neutral-900' : 'text-neutral-600'}`}
    >
      {text}
    </span>
  </li>
);

const BillingToggle = ({
  billingCycle,
  setBillingCycle,
}: {
  billingCycle: BillingCycle;
  setBillingCycle: (cycle: BillingCycle) => void;
}) => {
  return (
    <div className="flex items-center gap-1 p-1.5 bg-neutral-100 border-2 border-neutral-200 rounded-xl">
      <button
        onClick={() => setBillingCycle('monthly')}
        className={`px-6 py-2.5 font-bold text-sm transition-all rounded-lg ${
          billingCycle === 'monthly'
            ? 'bg-white border-2 border-neutral-900 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]'
            : 'hover:bg-neutral-200 text-neutral-600'
        }`}
      >
        Bulanan
      </button>
      <button
        onClick={() => setBillingCycle('annual')}
        className={`px-6 py-2.5 font-bold text-sm transition-all rounded-lg flex items-center gap-2 ${
          billingCycle === 'annual'
            ? 'bg-white border-2 border-neutral-900 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]'
            : 'hover:bg-neutral-200 text-neutral-600'
        }`}
      >
        Tahunan
        <span className="text-xs bg-neutral-900 text-white px-2 py-0.5 rounded font-bold uppercase tracking-wider">
          Hemat 20%
        </span>
      </button>
    </div>
  );
};

export function PricingCarousel({
  plans,
  billingCycle,
  showBillingToggle = true,
  onBillingCycleChange,
  onCTAClick,
  className = '',
  title,
  description,
}: PricingCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const scrollToIndex = (index: number) => {
    if (scrollRef.current) {
      const cardWidth = scrollRef.current.clientWidth * 0.85;
      const gap = 16;
      scrollRef.current.scrollTo({
        left: index * (cardWidth + gap),
        behavior: 'smooth',
      });
      setActiveIndex(index);
    }
  };

  useEffect(() => {
    const checkActiveIndex = () => {
      if (scrollRef.current) {
        const { scrollLeft, clientWidth } = scrollRef.current;
        const cardWidth = clientWidth * 0.85;
        const newIndex = Math.round(scrollLeft / cardWidth);
        setActiveIndex(Math.min(newIndex, plans.length - 1));
      }
    };

    const scrollEl = scrollRef.current;
    if (scrollEl) {
      scrollEl.addEventListener('scroll', checkActiveIndex);
      checkActiveIndex();
      return () => scrollEl.removeEventListener('scroll', checkActiveIndex);
    }
  }, [plans.length]);

  return (
    <div className={className}>
      {title && (
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h2 className="text-3xl md:text-5xl lg:text-5xl font-bold text-neutral-900 leading-[1.1] tracking-tight mb-4">
            {title}
          </h2>
          {description && <p className="text-lg text-neutral-600 leading-relaxed">{description}</p>}
        </div>
      )}

      {showBillingToggle && onBillingCycleChange && (
        <div className="mb-8 flex justify-center">
          <BillingToggle billingCycle={billingCycle} setBillingCycle={onBillingCycleChange} />
        </div>
      )}

      {/* Mobile Carousel */}
      <div className="md:hidden">
        <div
          ref={scrollRef}
          className="flex overflow-x-auto gap-4 pb-4 px-4 snap-x snap-mandatory scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {plans.map((plan, index) => (
            <div key={index} className="snap-start shrink-0 w-[85vw] max-w-[340px]">
              <PricingCardComponent plan={plan} onCTAClick={onCTAClick} />
            </div>
          ))}
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center gap-2 mt-2">
          {plans.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToIndex(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === activeIndex ? 'bg-neutral-900' : 'bg-neutral-300'
              }`}
              aria-label={`Geser ke kartu ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Desktop Grid: three plans sit in one row on wide screens instead of
          leaving the last card alone on a second row. */}
      <div
        className={`hidden md:grid md:grid-cols-2 gap-6 mx-auto ${
          plans.length === 3 ? 'lg:grid-cols-3 max-w-5xl' : 'max-w-4xl'
        }`}
      >
        {plans.map((plan, index) => (
          <PricingCardComponent key={index} plan={plan} onCTAClick={onCTAClick} />
        ))}
      </div>
    </div>
  );
}

// Internal PricingCard component
function PricingCardComponent({ plan, onCTAClick }: { plan: PricingPlan; onCTAClick?: (plan: PricingPlan) => void }) {
  return (
    <LandingCard
      variant={plan.isPopular ? 'elevated' : 'default'}
      padding="md"
      className={`flex flex-col h-full ${plan.bgClass || ''}`}
    >
      <h3 className="text-xl font-bold text-neutral-900 mb-2">{plan.title}</h3>
      <p className="text-sm text-neutral-600 mb-6 min-h-[40px]">{plan.description}</p>

      {!plan.withoutPrice && (
        <div className="mb-6 pb-6 border-b-2 border-neutral-200">
          {plan.price === 'Gratis' ? (
            <span className="text-4xl font-bold text-neutral-900 tracking-tighter">Gratis</span>
          ) : (
            <>
              {plan.originalPrice && (
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-neutral-500 line-through">
                    Rp {plan.originalPrice}
                  </span>
                </div>
              )}
              <div className="flex items-baseline gap-1">
                <span className="text-lg font-bold text-neutral-900">Rp</span>
                <span className="text-4xl font-bold text-neutral-900 tracking-tighter">
                  {plan.price}
                </span>
              </div>
              <div className="text-sm text-neutral-500 font-medium mt-1">{plan.period}</div>
              {plan.annualTotalText && (
                <p className="text-sm text-neutral-800 font-semibold mt-1">{plan.annualTotalText}</p>
              )}
              {plan.annualSavingsText && (
                <p className="text-sm text-emerald-600 font-bold mt-0.5">{plan.annualSavingsText}</p>
              )}
            </>
          )}
        </div>
      )}

      <ul className="space-y-1 mb-8 flex-1">
        {plan.features.map((feat, idx) => (
          <PricingFeature key={idx} text={feat} />
        ))}
        {plan.highlightedFeatures?.map((feat, idx) => (
          <PricingFeature key={`high-${idx}`} text={feat} isHighlighted />
        ))}
      </ul>

      <LandingButton
        variant={plan.isPopular ? 'primary' : 'secondary'}
        size="md"
        className="w-full justify-center"
        href={plan.href}
        loading={plan.isActionLoading}
        onClick={() => {
          onCTAClick?.(plan);
          plan.onAction?.();
        }}
      >
        {plan.ctaText}
      </LandingButton>
      {plan.actionError && (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {plan.actionError}
        </p>
      )}
    </LandingCard>
  );
}
